import { Box } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiAccountCircleLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { TransactionItem as TransactionItemInterface } from '@clients/transactionsClient';
import TimelineItemTemplate from '@components/accountTimeline/accountTimelineBlock/TimelineItemTemplate';
import TwoCurrencyTransaction from '@components/twoCurrencyTransaction/TwoCurrencyTransaction';
import { useAccount } from '@contexts/AccountProvider';

export interface TransactionItemProp {
    transaction: TransactionItemInterface;
    clickable?: boolean;
}

const TransactionItem = ({
    transaction,
    clickable = true,
}: TransactionItemProp) => {
    const { account } = useAccount();

    const { theme } = useTheme();
    const principalTransaction = transaction?.amount?.find(
        (a) => a?.type === 'PRINCIPAL',
    );
    const localTransaction = transaction?.amount?.find(
        (a) => a?.type === 'LOCAL',
    );
    const isLocalTransaction =
        localTransaction?.value == null ||
        (principalTransaction?.value === localTransaction?.value &&
            principalTransaction.currency === localTransaction.currency);

    const isCreditingAccount = transaction?.transaction_type?.credit;

    return (
        <TimelineItemTemplate
            highlightOnHover={clickable}
            urlToNavigateTo={
                account?.account_id && transaction.id
                    ? `/${account?.account_id}/transactions/${transaction.id}`
                    : ''
            }
            secondaryTitle={
                isLocalTransaction ? (
                    <CurrencyFormatter
                        currency={principalTransaction?.currency}
                        amount={principalTransaction?.value}
                        symbol={isCreditingAccount ? 'positive' : 'default'}
                        variant={isCreditingAccount ? 'success' : 'default'}
                    />
                ) : (
                    <TwoCurrencyTransaction
                        principalTransaction={principalTransaction}
                        localTransaction={localTransaction}
                        symbol={isCreditingAccount ? 'positive' : 'default'}
                        variant={isCreditingAccount ? 'success' : 'default'}
                    />
                )
            }
            secondarySubtitle={
                transaction.transaction_type.credit ? (
                    <FormattedMessage
                        id="AccountStatements.credit"
                        defaultMessage="Credit"
                    />
                ) : (
                    <FormattedMessage
                        id="AccountStatements.debit"
                        defaultMessage="Debit"
                    />
                )
            }
            primaryTitle={
                <Box data-testid="secondary-title">
                    {transaction?.merchant?.name ||
                        transaction?.soft_descriptor}
                </Box>
            }
            primarySubtitle={
                <>
                    <DateFormatter
                        date={transaction?.event_date}
                        format="shortDateWithTimeArb"
                    />
                    {transaction.merchant?.category?.description && (
                        <Box component="span">
                            {' • '}
                            {transaction.merchant?.category?.description}
                        </Box>
                    )}
                </>
            }
            avatar={<RiAccountCircleLine size={theme.spacing(3.25)} />}
        />
    );
};

export default TransactionItem;
